import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { GetUnratedCandidatesResponse, UnratedFeedbackCandidate } from 'app/data/types/feedback';
import { useApi } from 'app/shared/contexts/api';

export const UNRATED_FEEDBACK_CANDIDATES_QUERY_KEY = 'feedback-candidates-unrated';
export const getUnratedFeedbackCandidatesQueryKey = (): QueryKey => [UNRATED_FEEDBACK_CANDIDATES_QUERY_KEY];

/** Candidates change on the scale of completed transactions, not renders. */
const STALE_TIME = 60_000;

/**
 * Every candidate the caller has not rated yet.
 *
 * `GET /api/feedback-candidates/unrated` — one request per feed load, indexed by transaction here, so
 * every card on the page can carry its own rating state. Its predecessor, `GET /feedback-candidates`,
 * returns the single most recent candidate for the popup to act on, which capped the feed at one
 * rateable card no matter how the matching was written.
 *
 * Unrated means no rating row, so `UNSEEN`, `SEEN` and `DISMISSED` all appear and only `SUBMITTED`
 * drops out. A dismissal declined a prompt; it is not a refusal to ever rate the thing, and the feed
 * is a surface the user chose to open.
 *
 * Side-effect free, deliberately: the stars are not a prompt, and marking a candidate `SEEN` here
 * would suppress the popup somewhere else.
 *
 * Fetched separately from the feed rather than joined into it upstream, so the feed renders on its own
 * schedule and a slow or unavailable feedback-service costs stars rather than activity.
 */
export const useUnratedFeedbackCandidatesQuery = (): { candidates: UnratedFeedbackCandidate[] } => {
  const { apiUrl, getAuthHeaders } = useApi();

  const { data } = useQuery({
    queryKey: getUnratedFeedbackCandidatesQueryKey(),
    queryFn: async (): Promise<UnratedFeedbackCandidate[]> => {
      const payload = await apiRequest<GetUnratedCandidatesResponse, void, void>({
        // No `/api` prefix, unlike every other feedback call. The gateway maps
        // `/api/feedback-candidates/:uuid`, so the prefixed form captures `unrated` as a uuid and the
        // service answers 400 "uuid is expected" before this route is ever reached. Verified against
        // production: prefixed 400s, unprefixed 200s.
        url: `${apiUrl}/feedback-candidates/unrated`,
        method: HttpMethod.GET,
        headers: await getAuthHeaders(),
      });

      return !payload.isEmpty() ? payload.data.candidates ?? [] : [];
    },
    enabled: !!apiUrl,
    staleTime: STALE_TIME,
    retry: false,
  });

  return { candidates: data ?? [] };
};
